import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, LinkIcon, MailIcon, Share2Icon } from 'lucide-react';
import { ArticleCard } from '../components/insights/ArticleCard';
import { PageHero } from '../components/ui/PageHero';
import { articles } from '../data/insights';
import { usePageMeta } from '../hooks/usePageMeta';
import { NotFound } from './NotFound';
export function ArticleDetail() {
  const {
    slug
  } = useParams();
  const article = articles.find(a => a.slug === slug);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  usePageMeta(article ? article.title : 'Article not found', article ? article.excerpt : 'The article you are looking for could not be found.');
  if (!article) return <NotFound data-id="element-550" data-mp="tc5bohry9upj" />;
  const related = [...articles.filter(a => a.slug !== article.slug && a.category === article.category), ...articles.filter(a => a.slug !== article.slug && a.category !== article.category)].slice(0, 3);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    window.setTimeout(() => setCopyState('idle'), 2500);
  };
  const shareClass = 'inline-flex h-10 items-center gap-2 rounded-full border border-plum/15 px-4 text-sm text-plum/80 transition-colors duration-150 hover:border-copper hover:text-copper-deep';
  return <>
      <PageHero compact label={article.category} title={article.title} intro={article.excerpt} data-id="element-551" data-mp="1cd36xzzmf9xb" />
      <article className="bg-porcelain" data-id="element-552" data-mp="17ka5bkzg8hpg">
        <div className="mx-auto max-w-5xl px-6 pt-12 lg:px-10" data-id="element-553" data-mp="12n1ktdqtw1l1">
          <img src={article.image} alt={article.imageAlt} className="aspect-[16/9] w-full rounded-2xl object-cover" data-id="element-554" data-mp="12mi420cwzj8l" />
        </div>
        <div className="mx-auto grid max-w-5xl gap-12 px-6 py-14 lg:grid-cols-12 lg:px-10 lg:py-20" data-id="element-555" data-mp="1vbfgvnycwnty">
          <aside className="space-y-6 text-sm lg:col-span-3" data-id="element-556" data-mp="3pjjd6lzn7120">
            <Link to="/insights" className="inline-flex items-center gap-1.5 font-semibold text-plum/70 hover:text-copper-deep" data-id="element-557" data-mp="3pk2uq1ldrnfi">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-558" data-mp="3fthnxqvjzcli" />
              All insights
            </Link>
            <div className="border-t border-plum/10 pt-5" data-id="element-559" data-mp="3ks1bfvpsowfz">
              <p className="text-plum/55" data-id="element-560" data-mp="3kqhvk681wneq">Author</p>
              <p className="mt-0.5 font-medium text-plum" data-id="element-561" data-mp="36sjshwz6kdej">{article.author ?? 'To be confirmed'}</p>
              <p className="mt-4 text-plum/55" data-id="element-562" data-mp="3aun5wr5ets2g">Reading time</p>
              <p className="mt-0.5 font-medium text-plum" data-id="element-563" data-mp="2wulvajzcf69s">{article.readingTime}</p>
              {article.isSample && <p className="mt-4 inline-block rounded-full border border-plum/15 px-2.5 py-1 text-xs text-plum/65" data-id="element-564" data-mp="31tp32c6pjqlg">Sample editorial content</p>}
            </div>
          </aside>
          <div className="lg:col-span-8 lg:col-start-5" data-id="element-565" data-mp="v162phjp3hc6">
            {article.sections.map(section => <section key={section.heading} className="mb-10" data-id="element-566" data-mp="zzzhreemrgeg">
                <h2 className="text-2xl font-semibold text-aubergine" data-id="element-567" data-mp="2kkw8h6n0nlyd">{section.heading}</h2>
                {section.paragraphs.map(p => <p key={p} className="mt-4 max-w-prose text-[17px] leading-[1.8] text-plum/80" data-id="element-568" data-mp="2ua7g0ou98ik5">
                    {p}
                  </p>)}
              </section>)}
            <div className="mt-14 border-t border-plum/10 pt-8" data-id="element-569" data-mp="33avrpcw6e6tv">
              <p className="font-display text-sm font-semibold text-aubergine" data-id="element-570" data-mp="339175zotggqb">Share this article</p>
              <div className="mt-4 flex flex-wrap gap-3" data-id="element-571" data-mp="2ybj7z10lpfvi">
                <button type="button" onClick={copyLink} className={shareClass} data-id="element-572" data-mp="3d9bl5miods6j">
                  {copyState === 'copied' ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-573" data-mp="38b0axaogxfns" /> : <LinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-574" data-mp="3884fbt03f32i" />}
                  {copyState === 'copied' ? 'Link copied' : copyState === 'failed' ? 'Copy unavailable' : 'Copy link'}
                </button>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className={shareClass} data-id="element-575" data-mp="4z9bkxrwnnp6">
                  <Share2Icon className="h-4 w-4" aria-hidden="true" data-id="element-576" data-mp="a06rt4tcu7o1" />
                  LinkedIn
                  <span className="sr-only" data-id="element-577" data-mp="ev2ju4ylcv15">(opens in a new tab)</span>
                </a>
                <a href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(url)}`} className={shareClass} data-id="element-578" data-mp="ixz6q7v5zdaw">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-579" data-mp="3ncqn83nr88ox" />
                  Email
                </a>
              </div>
              <span className="sr-only" aria-live="polite" data-id="element-580" data-mp="3rdiwgwwmmdl7">
                {copyState === 'copied' ? 'Link copied to clipboard' : ''}
              </span>
            </div>
          </div>
        </div>
      </article>
      <section className="border-t border-plum/10 bg-limestone py-20" data-id="element-581" data-mp="2zmwb5ob9eo5q">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-582" data-mp="33o7sz7nw653d">
          <h2 className="text-2xl font-semibold text-aubergine" data-id="element-583" data-mp="33mmzlldbcs73">Related insights</h2>
          <div className="mt-10 grid gap-x-8 gap-y-14 md:grid-cols-3" data-id="element-584" data-mp="2w3dzyjum07v8">
            {related.map(a => <ArticleCard key={a.slug} article={a} data-id="element-585" data-mp="2r4kmusj8iivl" />)}
          </div>
        </div>
      </section>
    </>;
}